import { HatElement } from '@/components/hat-element.js';
import { assetTypesCache } from '@/services/asset-types-cache.js';
import { codesService } from '@/services/codes.js';
import type { AssetType } from '@/services/asset-types-cache.js';

class HatAdminAssetTypeProperties extends HatElement {
    private _assetTypes: AssetType[] = [];
    private _categoryLabels = new Map<string, string>();

    connectedCallback() {
        super.connectedCallback();
        this._renderShell();
        this._load();
    }

    async _load() {
        try {
            const [assetTypes, categoryCodes] = await Promise.all([
                assetTypesCache.getAll(),
                codesService.getDomain('assets.categories'),
            ]);
            this._assetTypes = assetTypes.filter(t => t.properties?.length);
            this._categoryLabels = new Map(categoryCodes.map(c => [c.code, c.label]));
        } catch {
            this._assetTypes = [];
        }
        this._renderContent();
    }

    _renderShell() {
        this.root.innerHTML = `
            <style>
                h2 {
                    color: white;
                    font-size: 1.4rem;
                    margin: 0 0 20px;
                    text-shadow: 0 2px 4px rgba(0,0,0,0.3);
                }

                .card {
                    background: white;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                    table-layout: fixed;
                }

                thead th {
                    text-align: left;
                    padding: 10px 16px;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    color: #999;
                    background: #f8f8f8;
                    border-bottom: 2px solid #eee;
                }

                tr.group-row td {
                    background: #f0f0f0;
                    padding: 7px 16px;
                    font-size: 0.78rem;
                    font-weight: 700;
                    font-family: monospace;
                    color: #444;
                    letter-spacing: 0.04em;
                    border-top: 1px solid #ddd;
                }

                tr.group-row:first-of-type td {
                    border-top: none;
                }

                .group-meta {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .group-label { font-weight: 700; font-family: monospace; font-size: 0.78rem; color: #444; }

                .category-badge {
                    font-size: 0.72rem;
                    font-weight: 600;
                    font-family: sans-serif;
                    padding: 1px 7px;
                    border-radius: 10px;
                    background: #e8eaf6;
                    color: #3949ab;
                    letter-spacing: 0.02em;
                }

                .item-count {
                    font-weight: 400;
                    font-family: sans-serif;
                    color: #888;
                    margin-left: auto;
                }

                td {
                    padding: 9px 16px;
                    border-bottom: 1px solid #f5f5f5;
                    font-size: 0.88rem;
                    vertical-align: middle;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                tr:last-child td { border-bottom: none; }

                .prop-label { font-weight: 500; }

                .prop-code {
                    font-family: monospace;
                    font-size: 0.82rem;
                    color: #888;
                    font-weight: 400;
                }

                .loading {
                    text-align: center;
                    padding: 40px;
                    color: rgba(255,255,255,0.7);
                }
            </style>

            <h2>Asset Type Properties</h2>
            <div id="content"><p class="loading">Loading…</p></div>
        `;
    }

    _renderContent() {
        const content = this.root.getElementById('content');
        if (!content) return;

        if (!this._assetTypes.length) {
            content.innerHTML = `<p class="loading">No asset types found.</p>`;
            return;
        }

        const rows = this._assetTypes.map(t => {
            const categoryLabel = t.category ? (this._categoryLabels.get(t.category) ?? t.category) : '';
            const count = t.properties?.length ?? 0;
            return `
                <tr class="group-row">
                    <td>
                        <div class="group-meta">
                            <span class="group-label">${t.description ?? t.code}</span>
                            ${categoryLabel ? `<span class="category-badge">${categoryLabel}</span>` : ''}
                            <span class="item-count">${count} propert${count !== 1 ? 'ies' : 'y'}</span>
                        </div>
                    </td>
                </tr>
                ${(t.properties ?? []).map(p => `
                    <tr>
                        <td><span class="prop-label">${p.description ?? p.code}</span> <span class="prop-code">(${p.code})</span></td>
                    </tr>
                `).join('')}
            `;
        }).join('');

        content.innerHTML = `
            <div class="card">
                <table>
                    <thead>
                        <tr><th>Property</th></tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>
        `;
    }
}

customElements.define('hat-admin-asset-type-properties', HatAdminAssetTypeProperties);